import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EntityManager } from '@dch/entity-manager';
import { of } from 'rxjs';
import {
  map,
  mergeMap,
  catchError,
  distinctUntilKeyChanged,
} from 'rxjs/operators';
import { ApplicationState } from '../reducers';
import { selectCustomer } from '../selectors/customer-account.selector';
import {
  BILL_DOWNLOADER_ENTITY,
  NEW_BILL_DOWNLOADER_ENTITY,
} from 'src/app/my-bill/config/my-bill-entity.config';
import {
  getNewPdfDownloadRequest,
  getPdfDownloadFailure,
  getPdfDownloadRequest,
  getPdfDownloadSuccess,
} from '../actions/pdf-download.actions';

@Injectable()
export class PdfDownloadEffects {
  getPdfDownload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPdfDownloadRequest),
      concatLatestFrom(() => this.store.select(selectCustomer)),
      mergeMap(([{ paId, documentId, documentDate }, customer]) =>
        EntityManager.getEntity<ArrayBuffer>(BILL_DOWNLOADER_ENTITY, {
          cdbId: customer?.customerId,
          paId,
          documentId,
          documentDate: encodeURIComponent(documentDate),
        }).pipe(
          distinctUntilKeyChanged('value'),
          map((entity) =>
            entity.value
              ? getPdfDownloadSuccess({
                  pdfResponse: entity.value,
                  entityUrl: entity.url,
                  version: entity.version,
                })
              : getPdfDownloadFailure()
          ),
          catchError(() => of(getPdfDownloadFailure()))
        )
      )
    )
  );

  getNewPdfDownload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getNewPdfDownloadRequest),
      concatLatestFrom(() => this.store.select(selectCustomer)),
      mergeMap(([{ paId, documentId, docType }, customer]) => {
        const parameters = {
          documentId,
          docType,
        };
        const requestBody = {
          requestContext: {
            requestorDetails: {
              registeredEndUserIdentifier: {
                id: customer?.customerId,
              },
            },
          },
          requestDetails: {
            cdbId: customer?.customerId,
            paId,
          },
        };
        return EntityManager.saveEntity<ArrayBuffer>({
          ...EntityManager.createEntity(NEW_BILL_DOWNLOADER_ENTITY, parameters),
          value: requestBody,
        }).pipe(
          distinctUntilKeyChanged('value'),
          map((entity) => {
            return entity.value
              ? getPdfDownloadSuccess({
                  pdfResponse: entity.value,
                  entityUrl: entity.url,
                  version: entity.version,
                })
              : getPdfDownloadFailure();
          }),
          catchError(() => of(getPdfDownloadFailure()))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<ApplicationState>
  ) {}
}
